import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';

export default function SalesEntry() {
  const [items, setItems] = useState([]);
  const [entries, setEntries] = useState({});
  const [saleDate, setSaleDate] = useState(new Date().toISOString().split('T')[0]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);
  const [foodOnly, setFoodOnly] = useState(true);
  const [recentSales, setRecentSales] = useState([]);
  const [tab, setTab] = useState('entry'); // 'entry' | 'history'

  useEffect(() => {
    api.get('/inventory').then(({ data }) => setItems(data)).catch(() => {});
    api.get('/sales?days=7').then(({ data }) => setRecentSales(data)).catch(() => {});
  }, []);

  const visibleItems = items.filter(i => !foodOnly || i.is_food);

  async function save() {
    const entriesToSave = Object.entries(entries)
      .filter(([, qty]) => qty !== '' && qty !== null && !isNaN(parseFloat(qty)))
      .map(([inventory_item_id, quantity_sold]) => ({ inventory_item_id, quantity_sold: parseFloat(quantity_sold) }));
    if (!entriesToSave.length) { setError('Enter at least one quantity.'); return; }
    setSaving(true); setError(null); setSaved(false);
    try {
      await api.post('/sales', { entries: entriesToSave, sale_date: saleDate });
      setSaved(true);
      setEntries({});
      const { data } = await api.get('/sales?days=7');
      setRecentSales(data);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  // Group recent sales by date
  const salesByDate = {};
  for (const s of recentSales) {
    const d = s.sale_date?.split('T')[0] || s.sale_date;
    if (!salesByDate[d]) salesByDate[d] = [];
    salesByDate[d].push(s);
  }

  return (
    <div>
      <div className="flex gap-2 mb-4">
        <button className={`btn btn-sm ${tab === 'entry' ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setTab('entry')}>Enter Sales</button>
        <button className={`btn btn-sm ${tab === 'history' ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setTab('history')}>Recent Sales</button>
      </div>

      {tab === 'entry' && (
        <div>
          <div className="card mb-4">
            <div className="card-header">
              <div className="flex items-center justify-between">
                <span>Daily Sales Entry</span>
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-1" style={{ fontSize: 12, cursor: 'pointer' }}>
                    <input type="checkbox" checked={foodOnly} onChange={e => setFoodOnly(e.target.checked)} />
                    Food only
                  </label>
                  <input type="date" value={saleDate} onChange={e => setSaleDate(e.target.value)}
                    style={{ fontSize: 13, background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 6, padding: '3px 8px', color: '#fff' }} />
                </div>
              </div>
            </div>
            <div className="card-body">
              {saved && <div className="alert alert-success mb-3">✓ Sales saved!</div>}
              {error && <div className="alert alert-error mb-3">{error}</div>}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 8 }}>
                {visibleItems.map(item => (
                  <div key={item.id} style={{ background: 'rgba(255,255,255,0.04)', borderRadius: 8, padding: '8px 10px' }}>
                    <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4, color: '#e2e8f0' }}>{item.name}</div>
                    <div className="flex items-center gap-2">
                      <input
                        type="number" min="0" step="0.1" placeholder="0"
                        value={entries[item.id] ?? ''}
                        onChange={e => setEntries(prev => ({ ...prev, [item.id]: e.target.value }))}
                        style={{ width: 70, fontSize: 13, background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 6, padding: '3px 6px', color: '#fff' }}
                      />
                      <span style={{ fontSize: 11, color: '#64748b' }}>{item.unit}</span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex justify-between items-center mt-4">
                <span className="text-sm text-gray">{Object.values(entries).filter(v => v !== '').length} items entered</span>
                <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>
                  {saving ? 'Saving...' : 'Save Sales'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {tab === 'history' && (
        <div>
          {Object.keys(salesByDate).length === 0 && (
            <div className="card"><div className="card-body text-gray" style={{ textAlign: 'center', padding: 32 }}>No sales recorded in the last 7 days.</div></div>
          )}
          {Object.entries(salesByDate).sort((a,b) => b[0].localeCompare(a[0])).map(([date, sales]) => (
            <div className="card mb-3" key={date}>
              <div className="card-header">{new Date(date + 'T12:00:00').toLocaleDateString('en', { weekday: 'long', month: 'short', day: 'numeric' })}</div>
              <div className="table-wrap">
                <table>
                  <thead><tr><th>Item</th><th>Qty Sold</th><th>Unit</th><th>Source</th></tr></thead>
                  <tbody>
                    {sales.map(s => (
                      <tr key={s.id}>
                        <td><strong>{s.item_name}</strong></td>
                        <td>{s.quantity_sold}</td>
                        <td className="text-gray">{s.unit || '—'}</td>
                        <td><span className="badge badge-gray">{s.source || 'manual'}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
